import {useCallback, useEffect, useState} from 'react';
import {api, useT} from '@kcast-fe/core';

import {pushService} from '../services';
import {getDeviceId, toastUtils} from '../utils';

export const usePushNotifications = () => {
  const {t} = useT();

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const registerDeviceMutation = api.devices.useRegisterDeviceMutation();
  const deleteDeviceMutation = api.devices.useDeleteDeviceMutation();
  const sendPushMutation = api.push.useSendPushMutation();

  const isSupported = pushService.isSupported();

  useEffect(() => {
    if (!isSupported) {
      return;
    }
    pushService.getSubscription().then((sub) => setIsSubscribed(Boolean(sub)));
  }, [isSupported]);

  const enable = useCallback(async () => {
    if (!isSupported) {
      toastUtils.error({message: t('toasts.notificationsUnsupported')});
      return;
    }

    setIsBusy(true);
    try {
      const token = await pushService.subscribe();
      if (!token) {
        const message =
          Notification.permission === 'denied'
            ? t('toasts.notificationsBlocked')
            : t('toasts.notificationsUnsupported');
        toastUtils.error({message});
        return;
      }

      await registerDeviceMutation.mutateAsync({
        token,
        platform: 'web',
        deviceId: getDeviceId()
      });
      setIsSubscribed(true);
    } catch (e) {
      console.error('[Push] Enable error:', e);
      toastUtils.error({message: t('toasts.notificationsError')});
    } finally {
      setIsBusy(false);
    }
  }, [isSupported, registerDeviceMutation, t]);

  const disable = useCallback(async () => {
    setIsBusy(true);
    try {
      const token = await pushService.unsubscribe();
      if (token) {
        await deleteDeviceMutation.mutateAsync(token);
      }
      setIsSubscribed(false);
    } catch {
      toastUtils.error({message: t('toasts.notificationsError')});
    } finally {
      setIsBusy(false);
    }
  }, [deleteDeviceMutation, t]);

  const toggle = useCallback((next: boolean) => (next ? enable() : disable()), [enable, disable]);

  // TEMP: send a test push to the current user for debugging
  const sendTest = useCallback(async () => {
    setIsBusy(true);
    try {
      await sendPushMutation.mutateAsync({
        title: 'Test push',
        body: 'This is a test push notification',
        url: window.location.origin
      });
    } catch {
      toastUtils.error({message: t('toasts.notificationsError')});
    } finally {
      setIsBusy(false);
    }
  }, [sendPushMutation, t]);

  return {isSupported, isSubscribed, isBusy, toggle, sendTest};
};
